import axios from 'axios'
import Cookies from 'js-cookie'
import { ElMessage } from 'element-plus'
import router from '@/router'

// 创建axios实例
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'https://www.echosphere.ltd',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = Cookies.get('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 添加请求时间戳
    config.headers['X-Request-Timestamp'] = Date.now()

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 处理成功响应
    if (response.data?.message) {
      // 如果有成功消息，可以在这里显示
      // ElMessage.success(response.data.message)
    }
    return response.data
  },
  async (error) => {
    const { response, config } = error

    // 处理401错误 - token过期
    if (response?.status === 401 && !config._retry) {
      config._retry = true

      try {
        // 尝试刷新token
        const refreshToken = Cookies.get('refresh_token')
        if (refreshToken) {
          const refreshResponse = await axios.post(
            '/work/auth/refresh',
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`
              }
            }
          )

          const { access_token } = refreshResponse.data
          Cookies.set('access_token', access_token)
          config.headers.Authorization = `Bearer ${access_token}`

          // 重试原始请求
          return api(config)
        }
      } catch (refreshError) {
        // 刷新token失败，清除认证信息并跳转到登录页
        Cookies.remove('access_token')
        Cookies.remove('refresh_token')
        Cookies.remove('user_info')

        ElMessage.error('登录已过期，请重新登录')

        // 跳转到登录页
        if (router.currentRoute.value.path !== '/auth/login') {
          router.push({
            path: '/auth/login',
            query: { redirect: router.currentRoute.value.fullPath }
          })
        }
      }
    }

    // 处理其他错误
    let errorMessage = '请求失败'

    if (response) {
      switch (response.status) {
        case 400:
          errorMessage = response.data?.error || '请求参数错误'
          break
        case 403:
          errorMessage = response.data?.error || '权限不足'
          break
        case 404:
          errorMessage = response.data?.error || '资源不存在'
          break
        case 500:
          errorMessage = response.data?.error || '服务器内部错误'
          break
        case 502:
          errorMessage = '网关错误'
          break
        case 503:
          errorMessage = '服务暂时不可用'
          break
        case 504:
          errorMessage = '网关超时'
          break
        default:
          errorMessage = response.data?.error || `请求失败 (${response.status})`
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = '请求超时，请检查网络连接'
    } else if (error.message === 'Network Error') {
      errorMessage = '网络错误，请检查网络连接'
    }

    // 显示错误消息（除了登录页面）
    if (router.currentRoute.value.path !== '/auth/login') {
      ElMessage.error(errorMessage)
    }

    return Promise.reject(error)
  }
)

// 文件上传配置
export const uploadConfig = {
  maxSize: 100 * 1024 * 1024, // 100MB
  allowedTypes: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/webp',
    'video/mp4',
    'video/avi',
    'video/mov',
    'video/wmv',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'application/zip',
    'application/x-rar-compressed',
    'application/x-7z-compressed'
  ],
  allowedExtensions: [
    '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp',
    '.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv',
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
    '.txt', '.zip', '.rar', '.7z'
  ]
}

// 验证文件类型
export const validateFile = (file) => {
  const { maxSize, allowedTypes } = uploadConfig

  // 检查文件大小
  if (file.size > maxSize) {
    return {
      valid: false,
      message: `文件大小不能超过 ${maxSize / 1024 / 1024}MB`
    }
  }

  // 检查文件类型
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      message: '不支持的文件类型'
    }
  }

  return { valid: true }
}

// 分片上传
export const chunkedUpload = async (file, onProgress, chunkSize = 5 * 1024 * 1024) => {
  const totalChunks = Math.ceil(file.size / chunkSize)
  const fileId = Date.now() + '-' + Math.random().toString(36).substr(2)

  let uploadedChunks = 0

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    const start = chunkIndex * chunkSize
    const end = Math.min(start + chunkSize, file.size)
    const chunk = file.slice(start, end)

    const formData = new FormData()
    formData.append('file', chunk)
    formData.append('fileId', fileId)
    formData.append('chunkIndex', chunkIndex)
    formData.append('totalChunks', totalChunks)
    formData.append('fileName', file.name)
    formData.append('fileSize', file.size)

    try {
      await api.post('/work/upload/chunk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      uploadedChunks++
      const progress = Math.round((uploadedChunks / totalChunks) * 100)

      if (onProgress) {
        onProgress(progress)
      }
    } catch (error) {
      throw new Error(`上传失败 (第 ${chunkIndex + 1} 个分片)`)
    }
  }

  // 合并分片
  try {
    const response = await api.post('/work/upload/merge', {
      fileId,
      fileName: file.name,
      totalChunks
    })

    return response
  } catch (error) {
    throw new Error('文件合并失败')
  }
}

export default api